import TableOnly from "components/DataTable/TableOnly";
import PaginationWithState from "components/Pagination/PaginationWithState";
import Spinner from "components/Spinner";
import { setNotification } from "features/Notification/notificationSlice";
import DeleteModalButton from "components/DeleteModalBtn/DeleteModalButton";
import { useCallback, useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch, useSelector } from "react-redux";
import authHeader from "services/auth-header";
import API from "services/axios";
import LeadAddressAddUpdate from "./LeadAddressAddUpdate";

function LeadAddress({ selectedLead, refreshFunc: refreshLead }) {
  const user = useSelector((state) => state.user?.userInfo);
  const dispatch = useDispatch();

  /*   All States
   ********************************************* */
  const [filterBy, setFilterBy] = useState({
    name: "Filter by All",
    value: null,
  });

  const [addressData, setAddressData] = useState([]);
  const [formData, setFormData] = useState({});
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  // Pagination/Search states
  const [pageLimit, setPageLimit] = useState(5);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");

  const [defaultAddress, setDefaultAddress] = useState(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadingOn = () => {
    setLoading(true);
  };
  const loadingOff = () => {
    setLoading(false);
  };

  /*   All Functions
   ********************************************* */
  const setDefaultShipAddress = async (address) => {
    if (!selectedLead?.leadId) return;
    let body = {
      addressId: address.addressId,
      id: address.id,
      isDefault: true,
    };

    try {
      loadingOn();
      const { data } = await API.patch(
        "/lead-address/set-default/" + selectedLead?.leadId,
        body,
        {
          headers: authHeader(),
        }
      );
      if (data?.success) {
        await getLeadAddresses();
        dispatch(
          setNotification({
            message: data.message,
            type: "success",
          })
        );
      }
      loadingOff();
    } catch (err) {
      loadingOff();
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Get Lead Addresses
  const getLeadAddresses = useCallback(
    async ({ clearSearch = false, query, page } = {}) => {
      if (!selectedLead?.leadId) return;
      try {
        setLoading(true);

        const usedQuery = clearSearch
          ? ""
          : typeof query !== "undefined"
          ? query
          : "";
        const usedPage = page ?? pageNumber;
        const { data } = await API.post(
          "/lead-addresses/list/" +
            selectedLead?.leadId +
            "?page=" +
            usedPage +
            "&limit=" +
            pageLimit,
          {
            query: usedQuery,
            filterBy:
              usedQuery && usedQuery !== "" ? filterBy.value : undefined,
          },
          { headers: authHeader() }
        );
        if (data?.success) {
          let addresses = data?.data || [];
          let _defaultAddress = data?.defaultAddress;
          setDefaultAddress(_defaultAddress || null);
          setAddressData(addresses || []);
          setTotalPages(data?.totalPages || 0);
        } else {
          dispatch(
            setNotification({
              message: data?.message || "Failed to fetch",
              type: "error",
            })
          );
        }
      } catch (err) {
        dispatch(setNotification({ message: err.message, type: "error" }));
      } finally {
        setLoading(false);
      }
    },
    [dispatch, pageNumber, pageLimit, filterBy.value, selectedLead]
  );

  // Sorting Up
  const sortUp = async (id) => {
    if (!selectedLead?.leadId) return;
    try {
      const { data } = await API.post(
        "/lead-address/sort-up",
        { addressId: id, leadId: selectedLead?.leadId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getLeadAddresses();
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Sorting Down
  const sortDown = async (id) => {
    if (!selectedLead?.leadId) return;
    try {
      const { data } = await API.post(
        "/lead-address/sort-down",
        { addressId: id, leadId: selectedLead?.leadId },
        { headers: authHeader() }
      );
      if (data.success) {
        await getLeadAddresses();
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  // Delete Lead Address
  const deleteLeadAddress = async (id) => {
    try {
      const { data } = await API.delete("/lead-address/" + id, {
        headers: authHeader(),
      });
      if (data.success) {
        await getLeadAddresses();
        dispatch(
          setNotification({
            message: "Deleted Successfully",
            type: "success",
          })
        );
        return;
      }
    } catch (err) {
      dispatch(
        setNotification({
          message: err.message,
          type: "error",
        })
      );
    }
  };

  /*   Table Functions
   ********************************************* */

  let filters = [
    { name: "Company Name", value: "companyName" },
    { name: "Address Line 1", value: "addressLine1" },
    { name: "Address Line 2", value: "addressLine2" },
    { name: "City", value: "city" },
    { name: "State", value: "state" },
    { name: "Zip", value: "zip" },
    { name: "Country", value: "country" },
  ];

  const btnLeft = () => (
    <div className="d-flex">
      <h4 className="border-secondary border-bottom">
        Lead Shipping Addresses
      </h4>
    </div>
  );

  const buttons = () => (
    <div className=" d-flex justify-content-end ">
      {/* FilterBy */}
      <button
        type="button"
        className="px-1 bg-primary btn text-dark bg-opacity-10 border border-primary rounded-end mx-2 rounded-start"
        data-bs-toggle="dropdown"
        aria-haspopup="true"
        aria-expanded="false"
      >
        {filterBy ? filterBy.name : "Filter by All"}{" "}
        <i className="mdi mdi-chevron-down"></i>
      </button>
      <div className="dropdown-menu">
        <button
          onClick={() => setFilterBy({ name: "Filter by All", value: null })}
          className="dropdown-item"
        >
          Filter by All
        </button>
        {filters.map((item, index) => (
          <button
            key={index}
            onClick={() => setFilterBy(item)}
            className="dropdown-item"
          >
            {item.name}
          </button>
        ))}
      </div>
    </div>
  );

  const table_head = [
    { name: "Company Name", value: "companyName" },
    { name: "Address Line 1", value: "addressLine1" },
    { name: "Address Line 2", value: "addressLine2" },
    { name: "City", value: "city" },
    { name: "State", value: "state" },
    { name: "Zip", value: "zip" },
    { name: "Country", value: "country" },
    { name: "Actions", value: "Actions" },
  ];

  const table_body = (item, index) => (
    <tr
      className={`${item?.isDefault ? "bg-primary bg-opacity-10 " : ""}`}
      key={item.id}
    >
      <td className="py-0 pe-1 align-middle">{item.companyName}</td>
      <td className="py-0 pe-1 align-middle">{item.addressLine1}</td>
      <td className="py-0 pe-1 align-middle">{item.addressLine2} </td>
      <td className="py-0 pe-1 align-middle">{item.city} </td>
      <td className="py-0 pe-1 align-middle">{item.state} </td>
      <td className="py-0 pe-1 align-middle">{item.zip} </td>
      <td className="py-0 pe-1 align-middle">{item.country} </td>
      <td className="align-middle py-0 text-end">
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              sortUp(item.addressId);
            }}
            disabled={index === 0}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-chevron-double-up p-0"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              sortDown(item.addressId);
            }}
            disabled={index === addressData?.length - 1}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-chevron-double-down"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              setFormData(item);
              setIsUpdateMode(true);
              setShowAddModal(true);
            }}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_delete") ||
          user?.roles === "admin") && (
          <DeleteModalButton
            deleteFunc={deleteLeadAddress}
            deleteParams={item.id}
            btnClass={"btn btn-sm px-1"}
            disableConditions={loading}
          />
        )}
      </td>
    </tr>
  );
  const topRow = () => (
    <tr
      className={`${
        defaultAddress?.isDefault ? "bg-primary bg-opacity-10 " : ""
      }`}
      key={defaultAddress?.id || defaultAddress?.addressId}
    >
      <td className="py-0 pe-1 align-middle">{defaultAddress?.companyName}</td>
      <td className="py-0 pe-1 align-middle">{defaultAddress?.addressLine1}</td>
      <td className="py-0 pe-1 align-middle">
        {defaultAddress?.addressLine2}{" "}
      </td>
      <td className="py-0 pe-1 align-middle">{defaultAddress?.city} </td>
      <td className="py-0 pe-1 align-middle">{defaultAddress?.state} </td>
      <td className="py-0 pe-1 align-middle">{defaultAddress?.zip} </td>
      <td className="py-0 pe-1 align-middle">{defaultAddress?.country} </td>
      <td className="align-middle py-0 text-end">
        {(user?.permissions?.includes("lead_update") ||
          user?.roles === "admin") && (
          <button
            onClick={() => {
              setFormData(defaultAddress);
              setIsUpdateMode(true);
              setShowAddModal(true);
            }}
            className="btn btn-sm px-1"
          >
            <i className="mdi mdi-pencil"></i>
          </button>
        )}
        {(user?.permissions?.includes("lead_delete") ||
          user?.roles === "admin") && (
          <DeleteModalButton
            deleteFunc={deleteLeadAddress}
            deleteParams={defaultAddress?.id}
            btnClass={"btn btn-sm px-1"}
            disableConditions={loading}
          />
        )}
      </td>
    </tr>
  );

  useEffect(() => {
    let timeout = setTimeout(() => {
      if (selectedLead) {
        getLeadAddresses();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, [selectedLead, getLeadAddresses]);

  useEffect(() => {
    if (!showAddModal) {
      setFormData({});
      setIsUpdateMode(false);
    }
  }, [showAddModal]);

  return (
    <div className="d-flex flex-column mt-2 flex-grow-1 ">
      {loading && <Spinner />}
      <div className="d-flex justify-content-between mb-1 align-items-center">
        <div className="d-flex align-items-center gap-5">{btnLeft()}</div>

        <div className="d-flex align-items-center gap-0">
          <div className="">
            Show &nbsp;
            <div className="btn-group">
              <button
                className="px-1 btn btn-outline-primary bg-primary bg-opacity-10 text-black"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {pageLimit} <i className="mdi mdi-chevron-down"></i>
              </button>
              <ul className="dropdown-menu w-25">
                <li onClick={() => setPageLimit(5)}>
                  <button className="dropdown-item">5</button>
                </li>
                <li onClick={() => setPageLimit(10)}>
                  <button className="dropdown-item">10</button>
                </li>
                <li onClick={() => setPageLimit(15)}>
                  <button className="dropdown-item">15</button>
                </li>
                <li onClick={() => setPageLimit(20)}>
                  <button className="dropdown-item">20</button>
                </li>
              </ul>
            </div>
          </div>
          {buttons()}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setPageNumber(1);
              if (searchTerm && searchTerm !== "") {
                getLeadAddresses({ query: searchTerm, page: 1 });
              } else {
                getLeadAddresses({ clearSearch: true, page: 1 });
              }
            }}
            className="d-flex justify-content-end gap-1 align-items-center"
          >
            <input
              type="search"
              placeholder="Search by name..."
              value={searchTerm}
              className="form-control"
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="btn btn-info" type="submit">
              Search
            </button>
            {searchTerm !== "" && (
              <button
                className="btn btn-outline-danger"
                type="button"
                onClick={() => {
                  setSearchTerm("");
                  getLeadAddresses({ clearSearch: true, page: 1 });
                }}
              >
                Clear
              </button>
            )}
          </form>
          <div className="">
            {(user?.permissions?.includes("lead_add") ||
              user?.roles === "admin") && (
              <button
                onClick={() => {
                  setShowAddModal(true);
                  setFormData({});
                  setIsUpdateMode(false);
                }}
                type="button"
                className="btn btn-primary  ms-1"
              >
                Add
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="" style={{ minHeight: "10vh", borderRadius: 15 }}>
        <div className="horizontal-scrollable d-flex flex-grow-1">
          <TableOnly
            table_head={table_head}
            table_body={table_body}
            table_data={addressData}
            disableSorting={true}
            actionsOnEnd={true}
            topRow={defaultAddress ? topRow() : null}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end mt-2">
        <PaginationWithState
          totalPages={totalPages}
          currPage={pageNumber}
          setCurrPage={setPageNumber}
        />
      </div>

      <Modal
        size="xl"
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
      >
        <Modal.Body
          className="shadow-lg bg-white px-3 "
          style={{ borderRadius: 15 }}
        >
          <LeadAddressAddUpdate
            leadId={selectedLead?.leadId}
            formData={formData}
            setFormData={setFormData}
            isUpdateMode={isUpdateMode}
            setIsUpdateMode={setIsUpdateMode}
            showModal={setShowAddModal}
            refreshList={getLeadAddresses}
            refreshLead={refreshLead}
            setPageNumber={setPageNumber}
            setDefaultShipAddress={setDefaultShipAddress}
          />
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default LeadAddress;
